import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useState } from "react";

import { apiPostMedications } from "../../../api";
import { ApiConnector, MedicationDto } from "../../../api/api-connector";
import {
  type SupportedMedications,
  useSupportedMedications,
} from "../../../catalogs/supported-medications";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Button } from "../../../components/ui-kit/button";
import { alert } from "../../../components/ui-kit/dialog-service";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { CellDate } from "../../../components/ui-kit/tables/cells/cell-date";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { CellText } from "../../../components/ui-kit/tables/cells/cell-text";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import {
  applyFrequencyUnitCountToPatch,
  frequencyUnitOf,
  frequencyUnits,
} from "../frequency-unit";
import { Patient } from "../patient";
import {
  GroupOnlyCheckbox,
  groupOnlyHint,
  type MedicationIdentity,
  MedicationGroupSelect,
  MedicationPicker,
  needsGroupSelection,
} from "./medication-name-fields";
import {
  applyHasEndedToPatch,
  draftHasEnded,
  MedicationEndedField,
  MedicationStartedField,
  setDraftHasEnded,
} from "./medication-period-fields";
import { Medications } from "./medications";
import { recreateMedicationPayload } from "./recreate-medication";

export const MedicationsDataTable = observer(
  function MedicationsDataTable(props: {
    data: MedicationDto[];
    api: ApiConnector;
    patient: Patient;
    onInvalidated?: () => void;
    disableEdit?: boolean;
    disableHistory?: boolean;
    disablePagination?: boolean;
    persistState?: boolean;
    filterActions?: ReactNode;
  }) {
    const { t } = useLingui();
    const medications = useSupportedMedications();
    const [model] = useState(() => {
      const m = new Medications(
        [],
        props.api.permissions,
        props.api.requireUserUUID(),
      );
      if (props.disableHistory) {
        m.hasHistory = false;
      }
      return m;
    });

    useEffect(() => {
      model.updateData(props.data);
    }, [props.data, model]);

    return (
      <DataTable
        filterActions={props.filterActions}
        persistState={props.persistState}
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "medication", other: "medications" })}`
        }
        columns={[
          {
            header: <Trans>Started</Trans>,
            attr: "medication_started",
            type: "date",
            editable: !props.disableEdit,
            sort: true,
            initSort: "desc",
            // Hosts the "Has ended" checkbox, which gates the Ended field
            // below. Only the edit dialog ever passes a `patch` for this
            // column, so the table itself still renders a plain date.
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <MedicationStartedField
                  model={patch}
                  hasEnded={draftHasEnded(patch, item.medication_ended)}
                  onHasEndedChange={(hasEnded) =>
                    setDraftHasEnded(patch, hasEnded)
                  }
                />
              ) : (
                <CellDate item={item} attr="medication_started" type="date" />
              ),
          },
          {
            header: <Trans>Ended</Trans>,
            attr: "medication_ended",
            type: "date",
            editable: !props.disableEdit,
            sort: true,
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <MedicationEndedField
                  model={patch}
                  hasEnded={draftHasEnded(patch, item.medication_ended)}
                  onHasEndedChange={(hasEnded) =>
                    setDraftHasEnded(patch, hasEnded)
                  }
                />
              ) : (
                <CellDate item={item} attr="medication_ended" type="date" />
              ),
          },
          {
            header: <Trans>Group</Trans>,
            attr: "medication_group",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Medication`,
              options: uniqueValueOptions("medication_group"),
            },
            // Editable so the group reaches the edit draft, which is what makes
            // correcting a mis-classified medication possible at all — see
            // `MedicationGroupField` for what the user actually gets to change.
            editable: !props.disableEdit,
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <MedicationGroupField
                  medications={medications}
                  patch={patch}
                  // Correcting the group replaces the record, so it needs the
                  // permission to delete the mistaken one. `item` is the row
                  // merged with the draft, but the rule reads only `user_uuid`,
                  // which the draft never touches.
                  canReplace={model.validateItemDelete(item) == null}
                />
              ) : (
                <CellText item={item} attr="medication_group" />
              ),
          },
          {
            header: <Trans>Name</Trans>,
            attr: "medication_name",
            type: "string",
            sort: true,
            // Any medication can be re-picked in the edit dialog, catalog
            // entries included — mistyping the drug is the mistake this is here
            // to fix. Re-picking sets the group too, and a group change is
            // applied by replacing the record (see `onEdit`).
            editable: !props.disableEdit,
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <span className="flex flex-col items-start gap-2">
                  <MedicationPicker medications={medications} model={patch} />
                  <span className="flex items-center gap-1">
                    <GroupOnlyCheckbox model={patch} label={t`Group only`} />
                    <HelperTooltip content={groupOnlyHint} />
                  </span>
                </span>
              ) : (
                <CellText item={item} attr="medication_name" />
              ),
          },
          {
            header: <Trans>Dose</Trans>,
            attr: "medication_dose",
            type: "number",
            editable: !props.disableEdit,
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "medication_unit",
            type: "string",
            editable: !props.disableEdit,
          },
          {
            header: <Trans>Dosage</Trans>,
            attr: "medication_dosage",
            editable: !props.disableEdit,
            editInDialog: true,
            // Driven by `patch` rather than the model's draft directly, so the
            // same editor serves the edit dialog and the inline "Change dosage"
            // flow.
            cell: ({ item, patch }) =>
              patch ? (
                <span className="inline-flex items-center gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <NumberInput
                      key={i}
                      className="number-input"
                      min={0}
                      value={patch.medication_dosage[i]}
                      onChange={(newValue) => {
                        patch.medication_dosage[i] = newValue ?? 0;
                      }}
                      width={50}
                    />
                  ))}
                </span>
              ) : (
                <span>{item.medication_dosage.join("/")}</span>
              ),
          },
          {
            header: <Trans>Frequency</Trans>,
            attr: "medication_frequency_regular_repeat_every",
            // Only interval-scheduled medications are editable here. A
            // day-scheduled one (`medication_frequency_on_days`) can't be
            // expressed by this editor, and setting an interval on it would
            // leave the record carrying two conflicting schedules — the API
            // accepts both fields at once. Gated on the saved row's day list,
            // which is what `editable` receives: the interval itself defaults
            // to 1 server-side even on a day-scheduled row, so it says nothing
            // about which half is active. The "Change dosage" flow names this
            // field too, and honours the same gate (see `singleEditing` in
            // editable-cell).
            editable: (item) =>
              !props.disableEdit && model.repeatsByInterval(item),
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <span className="inline-flex items-center gap-1">
                  {/* One message rather than "Every" + input + unit as three,
                      so a locale controls word order and case agreement — the
                      split read "Alle 3 Tag" in German. The unit is a select,
                      so the editor reads "Every 3 days" where the cell reads
                      "Every 3rd day": the noun cannot be both the label of a
                      choice and an ordinal-agreeing part of the sentence. */}
                  <Trans>
                    Every{" "}
                    <NumberInput
                      model={patch}
                      attr="medication_frequency_regular_repeat_every"
                      min={1}
                      // A repeat interval is a whole number of days or
                      // months; NumberInput otherwise defaults to step="any".
                      step={1}
                      // The column is only editable for a medication that
                      // already repeats, so an empty input is never a valid
                      // submission — and an undefined value would reach the
                      // API as a PUT with no body at all, saving nothing
                      // without saying so.
                      required
                      width={50}
                    />{" "}
                    <span className="inline-block w-28">
                      <Select
                        value={frequencyUnitOf(item)}
                        onChange={(unit) => {
                          patch.medication_frequency_regular_repeat_every_unit =
                            unit;
                        }}
                        values={frequencyUnits}
                        required
                        ariaLabel={t`Frequency unit`}
                      />
                    </span>
                  </Trans>
                </span>
              ) : (
                <span>{model.freqString(item)}</span>
              ),
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: true,
            editable: !props.disableEdit,
          },
          !props.disableEdit && {
            header: <Trans>Change dosage</Trans>,
            sticky: "right",
            width: 80,
            cell: ({ item }) => (
              <>
                {model.isEditingItem(item) &&
                model.editingDraft &&
                "medication_change_date" in model.editingDraft ? (
                  <DateInput
                    type="date"
                    model={model.editingDraft}
                    attr="medication_change_date"
                  />
                ) : (
                  !model.isEditingItem(item) && (
                    <Button
                      onClick={() => {
                        model.startChangingDosage(item);
                      }}
                      data-tooltip={t`Use when adapting dose, dosage and / or frequency for this medication.`}
                      variant="subtle"
                    >
                      <Trans>Change</Trans>
                    </Button>
                  )
                )}
              </>
            ),
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={
          props.disableEdit
            ? undefined
            : async (editedField, item) => {
                // Both write paths refuse a unit that names no interval, so
                // the interval travels with it — see the helper.
                const edited = applyFrequencyUnitCountToPatch(
                  editedField,
                  item,
                );

                if ("medication_change_date" in edited) {
                  await props.patient.updateItem(
                    "medications/change",
                    item.uuid,
                    edited,
                  );
                } else {
                  const body = applyHasEndedToPatch(edited);
                  // Toggling "Has ended" off and back on leaves the record as
                  // it was, so the patch holds nothing but the UI-only key —
                  // and nothing at all once that is stripped.
                  if (Object.keys(body).length === 0) {
                    return;
                  }
                  // The identity fields can both be emptied in the dialog:
                  // free-texting a name clears the group until one is picked,
                  // and unticking "Group only" clears the name. Neither is a
                  // record the API would take — a null group has no create
                  // schema to validate against, a null name is dropped by
                  // `updateItem` as an unset field — so say so instead of
                  // sending a request that fails or does nothing.
                  const corrected = { ...item, ...body };
                  if (
                    !corrected.medication_group ||
                    !corrected.medication_name
                  ) {
                    await alert(
                      t`A medication needs both a name — or the "Group only" marker — and a group. Nothing was saved.`,
                      t`Incomplete medication`,
                    );
                    return;
                  }
                  if ("medication_group" in body) {
                    // The group can't be updated — it's the discriminator on
                    // create and absent from the update schema — so a corrected
                    // group means a new record carrying the old one's data.
                    // Reachable with the group select hidden, by re-picking a
                    // medication from another group, so the permission the
                    // replacement needs is checked here too.
                    const notAuthorized = model.validateItemDelete(item);
                    if (notAuthorized != null) {
                      await alert(
                        t`Correcting the medication group replaces the record, which you are not allowed to delete. Nothing was saved.`,
                        notAuthorized,
                      );
                      return;
                    }
                    // Created first: if the delete then fails the user sees a
                    // duplicate and an error, rather than losing the record.
                    await apiPostMedications({
                      body: recreateMedicationPayload(item, body),
                    });
                    await props.patient.deleteItem("medications", item.uuid);
                    props.onInvalidated?.();
                    return;
                  }
                  await props.patient.updateItem(
                    "medications",
                    item.uuid,
                    body,
                    // Correcting a wrongly-entered medication includes taking
                    // its dates back off, so those nulls have to reach the API.
                    // Both are optional on the record, and emptying either
                    // input is the only way to say "unknown".
                    {
                      clearableKeys: ["medication_started", "medication_ended"],
                    },
                  );
                }
                props.onInvalidated?.();
              }
        }
        onDelete={
          props.disableEdit
            ? undefined
            : async (item) => {
                await props.patient.deleteItem("medications", item.uuid);
                props.onInvalidated?.();
              }
        }
        rowsPerPage={props.disablePagination ? Number.MAX_VALUE : 25}
      />
    );
  },
);

/**
 * The Group row of the edit dialog. A group whose medication came from the
 * catalog is shown read-only — it follows the name, and re-picking the
 * medication is how it changes. Only a free-text or group-only medication offers
 * the picker, matching the add form.
 *
 * A changed group is applied by replacing the record (the update endpoint has no
 * `medication_group`), which is worth saying before the user saves — and is why
 * `canReplace` withholds the picker from a user who can't delete the old record.
 */
const MedicationGroupField = observer(function MedicationGroupField({
  medications,
  patch,
  canReplace,
}: {
  medications: SupportedMedications;
  patch: MedicationIdentity;
  canReplace: boolean;
}) {
  // The dialog merges the draft into the row before handing it to a cell, so the
  // saved group can't be read from here — but the dialog mounts fresh per edit,
  // so the value at mount is the saved one.
  const [savedGroup] = useState(patch.medication_group);

  return (
    <div className="flex flex-col gap-1">
      {canReplace && needsGroupSelection(medications, patch) ? (
        <MedicationGroupSelect medications={medications} model={patch} />
      ) : (
        <span className="text-sm">{patch.medication_group}</span>
      )}
      {patch.medication_group !== savedGroup && (
        <span className="text-warning-foreground text-xs">
          <Trans>
            Changing the group replaces this record: a corrected medication is
            created carrying the same dates, dose, dosage, frequency and
            comment, and the mistaken one is deleted. Its edit history, its
            clinical-study visit links and its original author do not carry over
            — the new record is entered by you.
          </Trans>
        </span>
      )}
    </div>
  );
});
